import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Crown, BarChart3, Palette, Calendar, Phone, Image, TrendingUp, Sparkles } from "lucide-react";

// Rotating premium features to highlight
const PREMIUM_FEATURES = [
  {
    icon: BarChart3,
    title: "See Your Analytics",
    description: "Track views, favorites, and how customers find you",
    stat: "Premium venues get 3x more visibility"
  },
  {
    icon: Palette,
    title: "Custom Branding",
    description: "Stand out with your own brand colors on your listing",
    stat: "Branded venues have 40% higher engagement"
  },
  {
    icon: Calendar,
    title: "Workspace Booking",
    description: "Let customers book seats directly through your listing",
    stat: "Fill empty spaces & increase revenue"
  },
  {
    icon: Phone,
    title: "Contact Details",
    description: "Display your phone, email, and social media links",
    stat: "Direct inquiries increase bookings by 60%"
  },
  {
    icon: Image,
    title: "More Photos",
    description: "Showcase your venue with up to 5 photos (vs 2)",
    stat: "Venues with 5+ photos get 2x more saves"
  }
];

export default function PremiumMissingNotification({ pricing, onUpgrade, isPremium }) {
  const [isVisible, setIsVisible] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(0);
  const [hasBeenDismissed, setHasBeenDismissed] = useState(false);

  // Show notification after 30 seconds, rotate features every 10 seconds
  useEffect(() => {
    // Never show for premium venues
    if (isPremium) {
      setIsVisible(false);
      return;
    }
    
    // Check if already dismissed this session
    const dismissed = sessionStorage.getItem("premium_notification_dismissed");
    if (dismissed) {
      setHasBeenDismissed(true);
      return;
    }

    // Show after 30 seconds of being on the dashboard
    const showTimer = setTimeout(() => {
      // Double-check premium status before showing
      if (!isPremium) {
        setIsVisible(true);
      }
    }, 30000);

    return () => clearTimeout(showTimer);
  }, [isPremium]);

  // Hide immediately if venue becomes premium
  useEffect(() => {
    if (isPremium) {
      setIsVisible(false);
    }
  }, [isPremium]);

  // Rotate through features
  useEffect(() => {
    if (!isVisible) return;

    const rotateTimer = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % PREMIUM_FEATURES.length);
    }, 8000);

    return () => clearInterval(rotateTimer);
  }, [isVisible]);

  const handleDismiss = () => {
    setIsVisible(false);
    setHasBeenDismissed(true);
    sessionStorage.setItem("premium_notification_dismissed", "true");
  };

  const feature = PREMIUM_FEATURES[currentFeature];
  const Icon = feature.icon;

  if (hasBeenDismissed) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-4 right-4 z-50 max-w-sm"
        >
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-xl border border-amber-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">What You're Missing</span>
              </div>
              <button
                onClick={handleDismiss}
                className="p-1 text-white/80 hover:text-white hover:bg-white/20 rounded transition-colors"
                data-testid="dismiss-premium-notification"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentFeature}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-sm">{feature.title}</h4>
                    <p className="text-xs text-gray-600 mt-0.5">{feature.description}</p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-amber-700">
                      <TrendingUp className="w-3 h-3" />
                      <span>{feature.stat}</span>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Progress dots */}
              <div className="flex items-center justify-center gap-1.5 mt-4 mb-3">
                {PREMIUM_FEATURES.map((feature, index) => (
                  <div
                    key={feature.title}
                    className={`w-1.5 h-1.5 rounded-full transition-colors ${
                      index === currentFeature ? "bg-amber-500" : "bg-amber-200"
                    }`}
                  />
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={onUpgrade}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium text-sm hover:from-amber-600 hover:to-orange-600 transition-all shadow-md hover:shadow-lg"
                data-testid="premium-notification-upgrade"
              >
                <Crown className="w-4 h-4" />
                Upgrade to Premium - £{pricing?.premium_annual || 149}/year
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
